import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { IoCalendarClear, IoCloseOutline } from "react-icons/io5";
import { FaLocationDot } from "react-icons/fa6";
import { MdCalendarMonth, MdGroups, MdDiamond, MdOutlineStar } from "react-icons/md";
import { fetchHolidayPackageById } from "@/services/api/holidays";
import { formatIndianPrice } from "@/utils/formatIndianPrice";
import { getCurrencySymbol } from "@/utils/getCurrencySymbol";
import { isIndianUser } from "@/utils/getCountryCode";
import useBodyScrollLock from "@/hooks/useBodyScrollLock";
import styles from "./SmartSearchDrawer.module.css";
import { map } from "lodash";
import dayjs from "dayjs";

const STAY_OPTIONS = [
  { id: "comfort", title: "Comfort", sub: "3★ Star | Included" },
  { id: "premium", title: "Premium", sub: "4★ Star" },
  { id: "luxury", title: "Luxury", sub: "5★ Star" },
];

function asList(value) {
  return Array.isArray(value) ? value : [];
}

export default function SmartSearchDrawer({
  open,
  packageItem,
  summary,
  onClose,
}) {
  useBodyScrollLock(open);
  const bookHref = useMemo(() => {
    const slug = packageItem?.slug;
    if (!slug) return "#";
    const path = `/holiday/${slug}`;
    return isIndianUser() ? `/in${path}` : path;
  }, [packageItem?.slug]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div className={`${styles.root} ${open ? styles.rootOpen : ""}`.trim()}>
      <button
        type="button"
        className={styles.overlay}
        aria-label="Close package drawer"
        onClick={onClose}
      />
      <aside className={styles.drawer} aria-hidden={!open}>
        <div className={styles.header}>
          <h2 className={styles.title}>{packageItem?.packageName}</h2>
          <button type="button" className={styles.closeBtn} aria-label="Close" onClick={onClose}>
            <IoCloseOutline size={24} />
          </button>
        </div>

        <div className={styles.chips}>
          {summary?.destination ? (
            <span className={styles.chip}>
              <FaLocationDot size={10} />
              {summary.destination}
            </span>
          ) : null}
          {summary?.duration ? (
            <span className={styles.chip}>
              <IoCalendarClear size={10} />
              {summary.duration}
            </span>
          ) : null}
          {summary?.travelType ? (
            <span className={styles.chip}>
              <MdGroups size={14} />
              {summary.travelType}
            </span>
          ) : null}
          {summary?.experience ? (
            <span className={styles.chip}>
              <MdDiamond size={12} />
              {summary.experience}
            </span>
          ) : null}
        </div>

        <div className={styles.body}>
          <h3 className={styles.sectionTitle}>Your day-by-day</h3>
          {packageItem?.itinerary?.length ? (
            <div className={styles.timeline}>
              {packageItem.itinerary.map((day, index) => (
                <div key={day.itineraryId || day.day || index} className={styles.dayItem}>
                  <span className={styles.dayDot} />
                  <div>
                    <p className={styles.dayLabel}>
                      DAY {day.day || index + 1}

                    </p>
                    <p className={styles.dayText}>
                      {day.title || ""}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          <h3 className={styles.sectionTitle}>Stay Style</h3>
          <div className={styles.stayGrid}>
            {packageItem?.slabs.slice(0, 3).map((stay) => {
              const starCount = parseInt(stay?.slabName, 10)
              return (
                <div
                  key={stay.slabId}
                  className={`${styles.stayCard}`}
                >
                  <p className={styles.stayTitle}>
                    {starCount > 0
                      ? map(Array(starCount), (_, i) => (
                        <MdOutlineStar
                          key={i}
                          style={{ color: "#121212" }}
                        />
                      ))
                      : dayjs(stay.fromDate).format("DD-MM-YYYY")}
                  </p>
                  <p className={styles.staySub}>{stay.slabName}</p>
                </div>
              )
            })}
          </div>

          {/* <h3 className={styles.sectionTitle}>Add to Your Trip</h3>
          <div className={styles.addonList}>
            {ADDONS.map((addon) => (
              <div key={addon.id} className={styles.addonItem}>
                <span className={styles.addonIcon}>+</span>
                <div>
                  <p className={styles.addonTitle}>{addon.title}</p>
                  <p className={styles.addonSub}>{addon.sub}</p>
                </div>
              </div>
            ))}
          </div> */}
        </div>

        <div className={styles.footer}>
          <div>
            <p className={styles.starting}>
              Starting from
              <span className={styles.oldPrice}>
                {getCurrencySymbol("sub", 10)}
                {formatIndianPrice(packageItem?.displayCutPrice)}
              </span>
            </p>
            <p className={styles.price}>
              {getCurrencySymbol("main", 16)}
              {formatIndianPrice(packageItem?.price)}
              <span className={styles.perPerson}> / Person</span>
            </p>
          </div>
          <a href={bookHref} className={styles.bookBtn}>
            View Details
          </a>
        </div>
      </aside>
    </div>,
    document.body
  );
}
